import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import {
  buildCumulativeData, flattenForSnapshot, resolveDraft, buildForecastStructure, normalizeDecisions,
  getOrCreateDraft, saveDraft, commitForecastDraft, createRevisionFrom, discardDraft,
  loadForecastVersions,
} from '../lib/buildCostControlData'

const fmt = n => n == null ? '—' : Math.round(n).toLocaleString()
const fmtN = n => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function monthYearLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00')
  return `${MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

function monthsBetween(d1, d2) {
  const a = new Date(d1), b = new Date(d2)
  return Math.max(0, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1)
}

// ─── ETC Modal ─────────────────────────────────────────────────────────────────
// Unchanged from Phase 1: never writes to the database. Only reports the
// calculated etc/eac/method/inputs back via onDraftChange()/onClearDraft(),
// staged as unsaved edits on top of the current Draft.
function ETCModal({ row, draft, readOnly, onClose, onDraftChange, onClearDraft }) {
  const actual     = parseFloat(row.actual)     || 0
  const commitment = parseFloat(row.commitment) || 0
  const budget     = parseFloat(row.budget)     || 0

  const [method,      setMethod]      = useState(draft?.method || row.savedEac?.etc_method || 'manual')
  const [manual,      setManual]      = useState(draft?.manual_amount != null ? String(draft.manual_amount) : (row.savedEac?.manual_amount != null ? String(row.savedEac.manual_amount) : ''))
  const [burnSource,  setBurnSource]  = useState(row.savedEac?.burn_source || (commitment > 0 ? 'commitment' : 'actual'))
  const [avgStart,    setAvgStart]    = useState(row.savedEac?.avg_start || '')
  const [avgEnd,      setAvgEnd]      = useState(row.savedEac?.avg_end   || '')
  const [histStart,   setHistStart]   = useState(row.savedEac?.hist_start || '')
  const [histEnd,     setHistEnd]     = useState(row.savedEac?.hist_end   || '')
  const [histMonths,  setHistMonths]  = useState(row.savedEac?.hist_months || [])

  const poFirstDate = row.po_first_date
  const poLastDate  = row.po_last_date
  const poMonths    = (poFirstDate && poLastDate) ? monthsBetween(poFirstDate, poLastDate) : 0
  const burnBase    = burnSource === 'actual' ? actual : commitment
  const burnRate    = poMonths > 0 ? burnBase / poMonths : 0

  const etc = useMemo(() => {
    if (method === 'manual') return parseFloat(manual) || 0
    if (method === 'average') {
      if (!avgStart || !avgEnd) return 0
      const remainingMonths = monthsBetween(avgStart, avgEnd)
      return burnRate * remainingMonths
    }
    if (method === 'histogram') {
      return histMonths.reduce((s, m) => s + ((parseFloat(m.qty)||0) * (parseFloat(m.cost)||0)), 0)
    }
    return 0
  }, [method, manual, avgStart, avgEnd, histMonths, burnRate])

  const eac = actual + etc

  function generateMonths(start, end) {
    if (!start || !end) return
    const months = []
    const s = new Date(start + '-01')
    const e = new Date(end   + '-01')
    while (s <= e) {
      const key = s.toISOString().slice(0,7)
      const existing = histMonths.find(m => m.month === key)
      months.push(existing || { month: key, qty: '', cost: '' })
      s.setMonth(s.getMonth() + 1)
    }
    setHistMonths(months)
  }

  function updateHistMonth(idx, field, val) {
    setHistMonths(prev => prev.map((m, i) => i === idx ? { ...m, [field]: val } : m))
  }

  function stageDraft() {
    const key = row.sub_code || row.cost_code
    onDraftChange(key, {
      eac_amount: eac,
      etc_amount: etc,
      method,
      manual_amount: method === 'manual' ? (parseFloat(manual) || null) : null,
    })
    onClose()
  }

  function clearDraft() {
    const key = row.sub_code || row.cost_code
    onClearDraft(key)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB] sticky top-0 bg-[#EDE7DA] z-10">
          <div>
            <h2 className="text-base font-bold text-[#1C1A18]">Estimate to Complete (ETC) {readOnly && <span className="text-xs font-normal text-[#9CA3AF]">(read-only — committed revision)</span>}</h2>
            <p className="text-xs text-[#9CA3AF] mt-0.5">
              {row.cost_code && <span className="font-mono text-purple-600 mr-2">{row.cost_code}</span>}
              {row.sub_code  && <span className="font-mono text-[#6B7280] mr-2">{row.sub_code}</span>}
              {row.description}
            </p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
        </div>

        <div className="p-5 space-y-4">

          <div className="grid grid-cols-3 gap-3">
            {[{l:'Budget',v:budget,c:'text-[#1C1A18]'},{l:'Actual',v:actual,c:'text-[#1A7A4A]'},{l:'Commitment',v:commitment,c:'text-blue-600'}].map(k => (
              <div key={k.l} className="bg-white rounded-xl px-3 py-2.5 text-center">
                <div className="text-xs text-[#9CA3AF]">{k.l}</div>
                <div className={`font-bold text-sm ${k.c}`}>{fmtN(k.v)}</div>
              </div>
            ))}
          </div>

          {!readOnly && (
            <>
              <div>
                <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">ETC Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {[{v:'manual',l:'Manual'},{v:'average',l:'Average'},{v:'histogram',l:'Histogram'}].map(m => (
                    <button key={m.v} onClick={() => setMethod(m.v)}
                      className={`py-2.5 rounded-lg text-sm font-medium border transition-colors ${method===m.v ? 'bg-purple-600 text-white border-purple-600' : 'border-[#E5E7EB] text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                      {m.l}
                    </button>
                  ))}
                </div>
              </div>

              {method === 'manual' && (
                <div>
                  <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">ETC Amount</label>
                  <input type="number" placeholder="Enter ETC amount" value={manual}
                    onChange={e => setManual(e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                </div>
              )}

              {method === 'average' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-[#6B7280] font-semibold">Burn Rate Based On:</span>
                    {[{v:'commitment',l:'Commitment'},{v:'actual',l:'Actual'}].map(s => (
                      <button key={s.v} onClick={() => setBurnSource(s.v)}
                        className={`px-3 py-1 text-xs rounded-lg border transition-colors ${burnSource===s.v ? 'bg-blue-600 text-white border-blue-600' : 'border-[#E5E7EB] text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                        {s.l}
                      </button>
                    ))}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <div className="text-xs text-blue-600 font-semibold mb-2">A — Burn Rate (from PO / Petty Cash dates)</div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <div className="text-[#9CA3AF]">First PO</div>
                        <div className="font-mono font-bold text-[#1C1A18]">{poFirstDate || '—'}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[#9CA3AF]">Last PO</div>
                        <div className="font-mono font-bold text-[#1C1A18]">{poLastDate || '—'}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[#9CA3AF]">PO Months</div>
                        <div className="font-mono font-bold text-[#1C1A18]">{poMonths}</div>
                      </div>
                    </div>
                    <div className="mt-2 text-center">
                      <span className="text-xs text-blue-500">Burn Rate = {fmtN(burnBase)} ÷ {poMonths} = </span>
                      <span className="font-bold text-blue-700">{fmtN(burnRate)} / month</span>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-[#6B7280] font-semibold mb-2">B — Remaining Months (Start → End)</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-[#9CA3AF] mb-1">Start Date</label>
                        <input type="month" value={avgStart} onChange={e => setAvgStart(e.target.value)}
                          className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                      </div>
                      <div>
                        <label className="block text-xs text-[#9CA3AF] mb-1">End Date</label>
                        <input type="month" value={avgEnd} onChange={e => setAvgEnd(e.target.value)}
                          className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                      </div>
                    </div>
                    {avgStart && avgEnd && (
                      <div className="mt-2 text-center text-xs text-purple-600">
                        Remaining Months = <span className="font-bold">{monthsBetween(avgStart, avgEnd)}</span>
                        <span className="text-[#9CA3AF] ml-2">ETC = {fmtN(burnRate)} × {monthsBetween(avgStart, avgEnd)} = <span className="font-bold text-purple-700">{fmtN(etc)}</span></span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {method === 'histogram' && (
                <div className="space-y-3">
                  <div className="text-xs text-[#6B7280] font-semibold">Monthly Plan — ETC = Σ (Cost × Qty)</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-[#9CA3AF] mb-1">From</label>
                      <input type="month" value={histStart} onChange={e => setHistStart(e.target.value)}
                        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                    </div>
                    <div>
                      <label className="block text-xs text-[#9CA3AF] mb-1">To</label>
                      <input type="month" value={histEnd} onChange={e => setHistEnd(e.target.value)}
                        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                    </div>
                  </div>
                  {histStart && histEnd && histStart <= histEnd && (
                    <button onClick={() => generateMonths(histStart, histEnd)}
                      className="w-full py-2 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                      Generate {monthsBetween(histStart, histEnd)} Months
                    </button>
                  )}
                  {histMonths.length > 0 && (
                    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                          <tr>
                            <th className="text-left px-3 py-2 text-[#6B7280] font-semibold">Month</th>
                            <th className="text-right px-3 py-2 text-[#6B7280] font-semibold">Qty</th>
                            <th className="text-right px-3 py-2 text-[#6B7280] font-semibold">Cost/Unit</th>
                            <th className="text-right px-3 py-2 text-purple-600 font-semibold">ETC</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F3F4F6]">
                          {histMonths.map((m, i) => {
                            const rowETC = (parseFloat(m.qty)||0) * (parseFloat(m.cost)||0)
                            return (
                              <tr key={i}>
                                <td className="px-3 py-2 font-mono text-[#6B7280]">
                                  {new Date(m.month+'-01').toLocaleDateString('en-US',{month:'short',year:'2-digit'})}
                                </td>
                                <td className="px-3 py-2">
                                  <input type="number" placeholder="0" value={m.qty} onChange={e => updateHistMonth(i,'qty',e.target.value)}
                                    className="border border-[#E5E7EB] rounded px-2 py-1 text-xs w-full text-right focus:outline-none"/>
                                </td>
                                <td className="px-3 py-2">
                                  <input type="number" placeholder="0" value={m.cost} onChange={e => updateHistMonth(i,'cost',e.target.value)}
                                    className="border border-[#E5E7EB] rounded px-2 py-1 text-xs w-full text-right focus:outline-none"/>
                                </td>
                                <td className="px-3 py-2 text-right font-bold text-purple-600">{rowETC>0?fmtN(rowETC):'—'}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 flex flex-col items-center justify-center">
              <span className="text-xs text-purple-600 font-semibold uppercase tracking-wide">ETC {readOnly?'':'Preview'}</span>
              <span className="text-lg font-black text-purple-700">{fmtN(etc)}</span>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex flex-col items-center justify-center">
              <span className="text-xs text-amber-600 font-semibold uppercase tracking-wide">EAC (Actual + ETC)</span>
              <span className="text-lg font-black text-amber-700">{fmtN(eac)}</span>
            </div>
          </div>
        </div>

        {!readOnly && (
          <div className="px-5 pb-2">
            <p className="text-xs text-[#9CA3AF]">This edit is staged locally — press <b>Save Draft</b> in the toolbar to persist it, or <b>Commit</b> to make the whole Draft a permanent Revision.</p>
          </div>
        )}
        <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
          <button onClick={onClose} className="py-2 px-4 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA]">
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && draft && (
            <button onClick={clearDraft}
              className="py-2 px-4 border border-red-200 text-red-500 rounded-lg text-sm hover:bg-red-50">
              Clear Change
            </button>
          )}
          {!readOnly && (
            <button onClick={stageDraft}
              className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
              Stage Change
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Commit Modal ───────────────────────────────────────────────────────────
function CommitModal({ periodLabel, onClose, onConfirm, committing }) {
  const [notes, setNotes] = useState('')
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-5 border-b border-[#E5E7EB]">
          <h2 className="text-base font-bold text-[#1C1A18]">Commit Forecast — {periodLabel}</h2>
          <p className="text-xs text-[#9CA3AF] mt-1">This creates a new, permanent, immutable Revision. It can never be edited afterward — only reviewed, compared, or used as the basis for a new Revision.</p>
        </div>
        <div className="p-5 space-y-3">
          <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            placeholder="e.g. Initial forecast for this cycle"
            className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
        </div>
        <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
          <button onClick={onClose} disabled={committing} className="py-2 px-4 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA] disabled:opacity-50">Cancel</button>
          <button onClick={() => onConfirm(notes)} disabled={committing}
            className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 disabled:opacity-50">
            {committing ? 'Committing…' : '🔒 Commit Revision'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Create Revision Modal ──────────────────────────────────────────────────
// Includes Option C conflict handling (agreed explicitly in this project):
// if an active Draft already exists, present an explicit choice
// (Continue / Discard-and-create / Cancel) instead of letting the RPC's
// rejection surface as a raw error. The SQL function itself is unchanged.
function CreateRevisionModal({ sourceLabel, existingDraftInfo, onClose, onConfirm, onDiscardAndCreate, onContinueExistingDraft, creating }) {
  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())

  if (existingDraftInfo) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md">
          <div className="p-5 border-b border-[#E5E7EB]">
            <h2 className="text-base font-bold text-[#1C1A18]">An active Draft already exists</h2>
            <p className="text-xs text-[#6B7280] mt-2">
              A Draft ({existingDraftInfo.forecast_date || 'in progress'}) already exists for this project. A Draft
              represents unfinished work and is never silently discarded or reused for a different Revision lineage.
              Choose how to proceed:
            </p>
          </div>
          <div className="p-5 space-y-3">
            <button onClick={onContinueExistingDraft} disabled={creating}
              className="w-full py-2.5 px-4 border border-[#E5E7EB] rounded-lg text-sm text-left hover:bg-[#FAFAFA] disabled:opacity-50">
              <div className="font-semibold text-[#1C1A18]">Continue existing Draft</div>
              <div className="text-xs text-[#9CA3AF] mt-0.5">Close this dialog and keep working on the current Draft.</div>
            </button>
            <button onClick={onDiscardAndCreate} disabled={creating}
              className="w-full py-2.5 px-4 border border-red-200 rounded-lg text-sm text-left hover:bg-red-50 disabled:opacity-50">
              <div className="font-semibold text-red-700">Discard existing Draft and create new</div>
              <div className="text-xs text-[#9CA3AF] mt-0.5">Permanently discards the current Draft (including any saved content), then creates a new Draft seeded from <b>{sourceLabel}</b>.</div>
            </button>
          </div>
          <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
            <button onClick={onClose} disabled={creating} className="flex-1 py-2 px-4 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA] disabled:opacity-50">Cancel</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#EDE7DA] rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-5 border-b border-[#E5E7EB]">
          <h2 className="text-base font-bold text-[#1C1A18]">Create Revision</h2>
          <p className="text-xs text-[#9CA3AF] mt-1">
            A new editable Draft will be created, seeded from <b>{sourceLabel}</b>. {sourceLabel} itself remains permanent and unchanged forever.
          </p>
        </div>
        <div className="p-5 space-y-3">
          <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide">New Draft's Forecast Period</label>
          <div className="grid grid-cols-2 gap-3">
            <select value={month} onChange={e => setMonth(parseInt(e.target.value))}
              className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
              {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
            <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value) || year)}
              className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
          </div>
        </div>
        <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
          <button onClick={onClose} disabled={creating} className="py-2 px-4 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA] disabled:opacity-50">Cancel</button>
          <button onClick={() => onConfirm(month, year)} disabled={creating}
            className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-bold hover:bg-purple-700 disabled:opacity-50">
            {creating ? 'Creating…' : '✏️ Create Revision'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function IndirectForecast({ projectId }) {
  const { can } = useProjectAuth(projectId)
  const [costCodes,    setCostCodes]    = useState([])
  const [categories,   setCategories]   = useState([])
  const [activeCategory, setActiveCategory] = useState('all')
  const [budgetByCC,   setBudgetByCC]   = useState({})
  const [commitByCC,   setCommitByCC]   = useState({})
  const [actualByCC,   setActualByCC]   = useState({})
  const [poDatesByCC,  setPoDatesByCC]  = useState({})
  const [loading,      setLoading]      = useState(false)
  const [busy,          setBusy]          = useState(false)
  const [etcModal,      setEtcModal]      = useState(null)
  const [expandedCC,   setExpandedCC]   = useState(new Set())

  const [draftId, setDraftId] = useState(null)
  const [draftForecastDate, setDraftForecastDate] = useState(null)
  const [revisions, setRevisions] = useState([])
  const [selectedVersion, setSelectedVersion] = useState('draft')
  const [showCommitModal, setShowCommitModal] = useState(false)
  const [showRevisionModal, setShowRevisionModal] = useState(false)

  const [draftEdits, setDraftEdits] = useState({})
  const hasUnsavedEdits = Object.keys(draftEdits).length > 0
  const [draftPersistedRows, setDraftPersistedRows] = useState({})
  // Baseline — read-only reference to the source Revision (based_on_snapshot_id),
  // never copied into the Draft's own storage. See ADD_Forecast_Architecture.md
  // SS15 domain-modeling addendum. Filtered to is_forecasted=true rows only
  // (with the forecast_history_decision_flags fallback for pre-migration
  // source Revisions) — a row that was merely a frozen Budget-fallback in
  // the source Revision carries no real information and must not be
  // treated as an Effective Forecast here.
  const [baselineRows, setBaselineRows] = useState({})
  // The raw Forecast domain structure from buildForecastStructure() — Cost
  // Codes + Sub Codes with Budget/Actual/Commitment only, no Forecast
  // resolution baked in. This is what resolveDraft() consumes, identically
  // to what flattenForSnapshot() consumes at Commit — one structure, one
  // resolution engine, no adapters.
  const [forecastStructure, setForecastStructure] = useState([])

  const toggleCC = (code) => setExpandedCC(s => { const n=new Set(s); n.has(code)?n.delete(code):n.add(code); return n })

  function handleDraftChange(rowKey, values) {
    setDraftEdits(prev => ({ ...prev, [rowKey]: values }))
  }
  function handleClearDraft(rowKey) {
    setDraftEdits(prev => { const n = { ...prev }; delete n[rowKey]; return n })
  }

  useEffect(() => {
    function handler(e) {
      if (!hasUnsavedEdits) return
      e.preventDefault()
      e.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasUnsavedEdits])

  const load = useCallback(async () => {
    if (!projectId) return
    setLoading(true)

    const { data: cc } = await supabase.from('cost_codes')
      .select('id, code, description, level, parent_id, cost_type, indirect_category_id')
      .eq('project_id', projectId)
      .eq('is_active', true).order('code')
    function findTopLevelCostType(node, all) {
      if (parseInt(node.level) === 1) return node.cost_type
      let cur = node
      let hops = 0
      while (cur.parent_id && hops < 5) {
        const parent = all.find(p => p.id === cur.parent_id)
        if (!parent) return null
        if (parseInt(parent.level) === 1) return parent.cost_type
        cur = parent
        hops++
      }
      return null
    }
    const indirectCC = (cc||[]).filter(c => findTopLevelCostType(c, cc||[]) === 'indirect')
    setCostCodes(indirectCC)

    const { data: catData } = await supabase.from('indirect_categories')
      .select('id, name, sort_order').eq('project_id', projectId)
      .order('sort_order', { ascending: true, nullsFirst: false }).order('name')
    setCategories(catData || [])

    const cumulative = await buildCumulativeData(projectId)
    const bcc = {}
    const commitBySubCode = {}
    const actualBySubCode = {}
    for (const parent of (cumulative.indirectRows || [])) {
      bcc[parent.code] = parent.bac || 0
      if (!(parent.children || []).length) {
        commitBySubCode[parent.code] = parent.commitment || 0
        actualBySubCode[parent.code] = parent.totalActualCost || 0
      }
      for (const sub of (parent.children || [])) {
        commitBySubCode[sub.code] = sub.commitment || 0
        actualBySubCode[sub.code] = sub.totalActualCost || 0
      }
    }
    setBudgetByCC(bcc)
    setCommitByCC(commitBySubCode)
    setActualByCC(actualBySubCode)

    const allCodes = indirectCC.map(c => c.code)
    const poDatesBySubCode = {}
    if (allCodes.length) {
      const { data: lines } = await supabase.from('po_line_items')
        .select('sub_code, purchase_orders!inner(project_id, issue_date)')
        .eq('purchase_orders.project_id', projectId)
      for (const l of (lines||[])) {
        if (!l.sub_code) continue
        const poDate = l.purchase_orders?.issue_date
        if (!poDate) continue
        if (!poDatesBySubCode[l.sub_code]) poDatesBySubCode[l.sub_code] = { first: poDate, last: poDate }
        else {
          if (poDate < poDatesBySubCode[l.sub_code].first) poDatesBySubCode[l.sub_code].first = poDate
          if (poDate > poDatesBySubCode[l.sub_code].last)  poDatesBySubCode[l.sub_code].last  = poDate
        }
      }
      const { data: pcv } = await supabase.from('petty_cash_vouchers')
        .select('sub_code, date_of_voucher').eq('project_id', projectId)
      for (const p of (pcv||[])) {
        if (!p.sub_code || !p.date_of_voucher) continue
        const d = p.date_of_voucher.slice(0,10)
        if (!poDatesBySubCode[p.sub_code]) poDatesBySubCode[p.sub_code] = { first: d, last: d }
        else {
          if (d < poDatesBySubCode[p.sub_code].first) poDatesBySubCode[p.sub_code].first = d
          if (d > poDatesBySubCode[p.sub_code].last)  poDatesBySubCode[p.sub_code].last  = d
        }
      }
    }
    setPoDatesByCC(poDatesBySubCode)

    const now = new Date()
    const newDraftId = await getOrCreateDraft(projectId, 'indirect', now.getMonth() + 1, now.getFullYear())
    setDraftId(newDraftId)

    const { data: draftRow } = await supabase.from('forecast_snapshots')
      .select('forecast_date, based_on_snapshot_id').eq('id', newDraftId).single()
    setDraftForecastDate(draftRow?.forecast_date || null)

    // ─── Baseline (ADD_Forecast_Architecture.md SS15 domain-modeling
    // addendum) — a READ-ONLY REFERENCE to the source Revision via
    // based_on_snapshot_id, never a copied dataset. Filtered to rows that
    // were genuinely is_forecasted=true in the source Revision — a row
    // that was merely a frozen Budget-fallback there (is_forecasted=false)
    // carries no real information and must not grant an Effective Forecast
    // here, or every ancestor Cost Code would incorrectly believe
    // something is forecasted underneath it and stop falling back to
    // Budget correctly.
    let baseline = {}
    if (draftRow?.based_on_snapshot_id) {
      const { data: baselineHistoryRows } = await supabase.from('forecast_history')
        .select('row_key, eac_amount, etc_amount, method, manual_amount, is_forecasted')
        .eq('snapshot_id', draftRow.based_on_snapshot_id)

      // Decision Layer flag resolution for pre-migration source Revisions
      // (ADD_Forecast_Architecture.md Addendum SS14) — mirrors
      // loadForecastHistory()'s resolveIsForecasted() exactly.
      const { data: flagData } = await supabase
        .from('forecast_history_decision_flags')
        .select('row_key, is_forecasted')
        .eq('snapshot_id', draftRow.based_on_snapshot_id)
      const flagByRowKey = {}
      for (const f of (flagData || [])) flagByRowKey[f.row_key] = f.is_forecasted

      for (const r of (baselineHistoryRows || [])) {
        const resolvedIsForecasted = flagByRowKey[r.row_key] !== undefined
          ? flagByRowKey[r.row_key]
          : (r.is_forecasted === true)
        if (!resolvedIsForecasted) continue
        baseline[r.row_key] = { eac_amount: r.eac_amount, etc_amount: r.etc_amount, method: r.method, manual_amount: r.manual_amount }
      }
    }
    setBaselineRows(baseline)

    // The raw Forecast domain structure — same function flattenForSnapshot()
    // uses at Commit, so the Draft view and the committed Snapshot are
    // always resolved from identical inputs (ADD SS15).
    const structure = await buildForecastStructure(projectId, 'indirect')
    setForecastStructure(structure)

    const { data: draftHistoryRows } = await supabase.from('forecast_history')
      .select('row_key, eac_amount, etc_amount, method, manual_amount')
      .eq('snapshot_id', newDraftId)
    const persisted = {}
    for (const r of (draftHistoryRows || [])) {
      persisted[r.row_key] = { eac_amount: r.eac_amount, etc_amount: r.etc_amount, method: r.method, manual_amount: r.manual_amount }
    }
    setDraftPersistedRows(persisted)
    setDraftEdits({})

    const { revisions: revList } = await loadForecastVersions(projectId, 'indirect')
    setRevisions(revList)

    setLoading(false)
  }, [projectId])

  useEffect(() => { load() }, [load])

  const tree = useMemo(() => {
    const parents  = costCodes.filter(c => !c.parent_id)
    const children = costCodes.filter(c =>  c.parent_id)
    return parents.map(p => ({
      ...p,
      subs: children.filter(c => c.parent_id === p.id),
      budget: budgetByCC[p.code] || 0,
    }))
  }, [costCodes, budgetByCC])

  const filteredTree = useMemo(() => {
    if (activeCategory === 'all') return tree
    if (activeCategory === 'uncategorized') {
      return tree.filter(cc => !cc.indirect_category_id || !categories.some(c => c.id === cc.indirect_category_id))
    }
    return tree.filter(cc => cc.indirect_category_id === activeCategory)
  }, [tree, activeCategory, categories])

  const viewingDraft = selectedVersion === 'draft'
  const selectedRevision = viewingDraft ? null : revisions.find(r => r.id === selectedVersion)

  const [revisionRows, setRevisionRows] = useState({}) // { row_key: { eac, etc, is_forecasted } }
  useEffect(() => {
    if (viewingDraft || !selectedVersion) { setRevisionRows({}); return }
    let cancelled = false
    ;(async () => {
      const { data } = await supabase.from('forecast_history')
        .select('row_key, eac_amount, etc_amount, is_forecasted').eq('snapshot_id', selectedVersion)
      if (cancelled) return

      // Decision Layer flag resolution for pre-migration rows
      // (ADD_Forecast_Architecture.md Addendum SS14) — mirrors Direct
      // Forecast's identical pattern exactly.
      const { data: flagData } = await supabase
        .from('forecast_history_decision_flags')
        .select('row_key, is_forecasted')
        .eq('snapshot_id', selectedVersion)
      if (cancelled) return
      const flagByRowKey = {}
      for (const f of (flagData || [])) flagByRowKey[f.row_key] = f.is_forecasted

      const rows = {}
      for (const r of (data || [])) {
        const resolvedIsForecasted = flagByRowKey[r.row_key] !== undefined
          ? flagByRowKey[r.row_key]
          : (r.is_forecasted === true)
        rows[r.row_key] = { eac: r.eac_amount, etc: r.etc_amount, is_forecasted: resolvedIsForecasted }
      }
      setRevisionRows(rows)
    })()
    return () => { cancelled = true }
  }, [viewingDraft, selectedVersion])

  // ─── resolveDraft() consumption (ADD_Forecast_Architecture.md SS15) ────
  // This is now the ONLY place Forecast (EAC) ownership/aggregation is
  // decided in Draft mode — the same pure resolveDraft() function
  // flattenForSnapshot() calls at Commit. No independent ownership logic
  // remains in this component.
  const resolvedTree = useMemo(() => {
    if (!viewingDraft) return []
    const merged = { ...draftPersistedRows, ...draftEdits }
    return resolveDraft(forecastStructure, baselineRows, merged)
  }, [viewingDraft, forecastStructure, baselineRows, draftPersistedRows, draftEdits])

  // Flat lookup by row_key (code) — { eac, isOwner } for every Cost Code
  // and Sub Code resolveDraft() resolved.
  const resolvedByCode = useMemo(() => {
    const map = {}
    for (const parent of resolvedTree) {
      for (const child of (parent.children || [])) {
        map[child.code] = { eac: child.resolvedEac || 0, isOwner: child.isOwner === true }
      }
      map[parent.code] = { eac: parent.resolvedEac || 0, isOwner: parent.isOwner === true }
    }
    return map
  }, [resolvedTree])

  function effectiveEac(rowKey) {
    if (viewingDraft) return resolvedByCode[rowKey]?.eac || 0
    if (revisionRows[rowKey] != null) return parseFloat(revisionRows[rowKey].eac) || 0
    return 0
  }
  function effectiveEtc(rowKey) {
    if (viewingDraft) {
      const actual = actualByCC[rowKey] || 0
      return effectiveEac(rowKey) - actual
    }
    if (revisionRows[rowKey] != null && revisionRows[rowKey].etc != null) return parseFloat(revisionRows[rowKey].etc) || 0
    return 0
  }
  function hasForecastEntry(rowKey) {
    // hasEntry() answers ONLY "is there a Current Draft Decision on this
    // row?" (drives the • forecast indicator only) — never "does a
    // Baseline or aggregated value exist here" (ADD SS15.4's three
    // independent concepts: Current Decision / Effective Forecast /
    // Ownership must never be collapsed into one signal again). In
    // Revision mode, every forecastable node has a Snapshot Layer row
    // (Philosophy A — complete statement, ADD SS5/SS6), including nodes
    // that were never actually forecasted (frozen Budget-fallback) — row
    // existence alone cannot answer "was this a real decision," only
    // is_forecasted can.
    if (viewingDraft) return resolvedByCode[rowKey]?.isOwner === true
    return revisionRows[rowKey]?.is_forecasted === true
  }

  function ccActualOf(cc) {
    return cc.subs.length ? cc.subs.reduce((s,sub)=>s+(actualByCC[sub.code]||0),0) : (actualByCC[cc.code]||0)
  }
  function ccCommitOf(cc) {
    return cc.subs.length ? cc.subs.reduce((s,sub)=>s+(commitByCC[sub.code]||0),0) : (commitByCC[cc.code]||0)
  }

  // ccEac()/ccEtc() now simply read the already-fully-resolved value from
  // effectiveEac() — in Draft mode this delegates directly to
  // resolveDraft()'s output, which has already applied ownership +
  // aggregation (via Effective Forecast eligibility, not Ownership
  // eligibility — ADD SS15.4) for every node. No aggregation or ownership
  // branching happens here anymore.
  function ccEac(cc) { return effectiveEac(cc.code) }
  function ccEtc(cc) { return ccEac(cc) - ccActualOf(cc) }

  const gtBudget = filteredTree.reduce((s,cc)=>s+cc.budget,0)
  const gtCommit = filteredTree.reduce((s,cc)=>s+ccCommitOf(cc),0)
  const gtActual = filteredTree.reduce((s,cc)=>s+ccActualOf(cc),0)
  const gtEAC    = filteredTree.reduce((s,cc)=>s+ccEac(cc),0)
  const gtETC    = gtEAC - gtActual

  async function handleSaveDraft() {
    if (!hasUnsavedEdits) return
    setBusy(true)
    try {
      const merged = { ...draftPersistedRows, ...draftEdits }
      // Ownership normalization (ADD_Forecast_Architecture.md SS16) — runs
      // exactly once, here, at the moment the Draft becomes a coherent
      // persisted state. See DirectForecast.jsx's identical handling for
      // the full rationale.
      const normalizedMerged = normalizeDecisions(merged, forecastStructure)
      const rows = await flattenForSnapshot(projectId, 'indirect', normalizedMerged, baselineRows)
      await saveDraft(draftId, rows)
      setDraftPersistedRows(normalizedMerged)
      setDraftEdits({})
      alert('Draft saved.')
    } catch (e) {
      alert('Failed to save draft: ' + (e.message || e))
    } finally {
      setBusy(false)
    }
  }

  async function handleCommit(notes) {
    setBusy(true)
    try {
      const merged = { ...draftPersistedRows, ...draftEdits }
      const normalizedMerged = normalizeDecisions(merged, forecastStructure)
      const rows = await flattenForSnapshot(projectId, 'indirect', normalizedMerged, baselineRows)
      // TEMPORARY DIAGNOSTIC — remove after verifying EV pipeline
      const evRows = rows.filter(r => r.row_key === '31.00024' || r.row_key === '32.00114')
      console.log('[EV DEBUG] handleCommit — rows about to be sent to saveDraft:', evRows)
      console.log('[EV DEBUG] handleCommit — full row object sample:', JSON.stringify(evRows, null, 2))
      // END TEMPORARY DIAGNOSTIC
      await saveDraft(draftId, rows)
      await commitForecastDraft(draftId, notes || null)
      setShowCommitModal(false)
      await load()
      setSelectedVersion('draft')
      alert('Forecast committed as a new Revision.')
    } catch (e) {
      alert('Failed to commit: ' + (e.message || e))
    } finally {
      setBusy(false)
    }
  }

  async function handleDiscardDraftEdits() {
    if (!window.confirm(`Discard ${Object.keys(draftEdits).length} unsaved change(s) in this session? (Already-saved Draft content is not affected.)`)) return
    setDraftEdits({})
  }

  async function handleDiscardEntireDraft() {
    if (!window.confirm('Discard the ENTIRE Draft, including any saved content? This cannot be undone.')) return
    setBusy(true)
    try {
      await discardDraft(draftId)
      await load()
    } catch (e) {
      alert('Failed to discard draft: ' + (e.message || e))
    } finally {
      setBusy(false)
    }
  }

  // ─── Create Revision — Option C conflict handling ──────────────────────
  const [pendingRevisionParams, setPendingRevisionParams] = useState(null)

  async function handleCreateRevision(month, year) {
    setBusy(true)
    try {
      await createRevisionFrom(projectId, 'indirect', selectedVersion, month, year)
      setShowRevisionModal(false)
      setPendingRevisionParams(null)
      await load()
      setSelectedVersion('draft')
      alert('New Draft created from that Revision.')
    } catch (e) {
      const msg = e.message || String(e)
      if (msg.includes('A Draft already exists')) {
        setPendingRevisionParams({ month, year })
      } else {
        alert('Failed to create revision: ' + msg)
      }
    } finally {
      setBusy(false)
    }
  }

  async function handleContinueExistingDraft() {
    setShowRevisionModal(false)
    setPendingRevisionParams(null)
    setSelectedVersion('draft')
  }

  async function handleDiscardAndCreateRevision() {
    if (!pendingRevisionParams) return
    if (!window.confirm('Discard the current Draft (including any saved content) and create a new Draft from the selected Revision? This cannot be undone.')) return
    setBusy(true)
    try {
      await discardDraft(draftId)
      await createRevisionFrom(projectId, 'indirect', selectedVersion, pendingRevisionParams.month, pendingRevisionParams.year)
      setShowRevisionModal(false)
      setPendingRevisionParams(null)
      await load()
      setSelectedVersion('draft')
      alert('Previous Draft discarded. New Draft created from that Revision.')
    } catch (e) {
      alert('Failed to discard draft and create revision: ' + (e.message || e))
    } finally {
      setBusy(false)
    }
  }

  if (!projectId) return null

  const currentSourceLabel = viewingDraft
    ? (draftForecastDate ? `Draft (${monthYearLabel(draftForecastDate)})` : 'Draft')
    : (selectedRevision ? `${monthYearLabel(selectedRevision.forecast_date)} Rev.${selectedRevision.revision_number}` : '')

  return (
    <div className="p-4 flex flex-col gap-4">

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1C1A18]">Indirect Forecast</h2>
        <button onClick={load} disabled={loading}
          className="px-4 py-2 bg-[#312E80] text-white text-sm rounded-lg hover:bg-[#3730a3] disabled:opacity-50">
          {loading ? '...' : '🔄 Refresh'}
        </button>
      </div>

      {/* ── Version Selector + Toolbar ─────────────────────────────────── */}
      <div className="rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Forecast Version</label>
          <select value={selectedVersion} onChange={e => setSelectedVersion(e.target.value)}
            className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-sm font-medium min-w-[220px]">
            <option value="draft">✏️ {draftForecastDate ? `Draft (${monthYearLabel(draftForecastDate)})` : 'Draft'}</option>
            {revisions.length > 0 && <option disabled>──────────</option>}
            {revisions.map(r => (
              <option key={r.id} value={r.id}>
                🔒 {monthYearLabel(r.forecast_date)} Rev.{r.revision_number}{r.isCurrent ? ' (Current)' : ''}
              </option>
            ))}
          </select>
        </div>

        {viewingDraft ? (
          <div className="flex items-center gap-2 flex-wrap">
            {hasUnsavedEdits && (
              <span className="text-xs font-semibold text-purple-700 flex items-center gap-1">
                <span className="text-purple-500">⚠</span> {Object.keys(draftEdits).length} unsaved change{Object.keys(draftEdits).length>1?'s':''}
              </span>
            )}
            <button onClick={handleDiscardDraftEdits} disabled={busy || !hasUnsavedEdits || !can('Forecast.Save')}
              className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-xs font-medium hover:bg-red-50 disabled:opacity-40">
              Discard Edits
            </button>
            <button onClick={handleDiscardEntireDraft} disabled={busy || !can('Forecast.Save')}
              className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-xs font-medium hover:bg-red-50 disabled:opacity-40">
              Discard Draft
            </button>
            <button onClick={handleSaveDraft} disabled={busy || !hasUnsavedEdits || !can('Forecast.Save')}
              className="px-3 py-1.5 border border-purple-300 text-purple-700 rounded-lg text-xs font-bold hover:bg-purple-50 disabled:opacity-40">
              💾 Save Draft
            </button>
            {can('Forecast.Commit') && (
              <button onClick={() => setShowCommitModal(true)} disabled={busy}
                className="px-4 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-bold hover:bg-purple-700 disabled:opacity-50">
                🔒 Commit
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#9CA3AF]">🔒 Read-only — committed{selectedRevision?.notes ? `: "${selectedRevision.notes}"` : ''}</span>
            {can('Forecast.Save') && (
              <button onClick={() => setShowRevisionModal(true)} disabled={busy}
                className="px-4 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-bold hover:bg-purple-700 disabled:opacity-50">
                ✏️ Create Revision
              </button>
            )}
          </div>
        )}
      </div>

      {categories.length > 0 && (
        <div className="inline-flex gap-1 p-1 bg-[#F3F4F6] rounded-xl self-start">
          {[
            { key: 'all', label: 'All' },
            ...categories.map(c => ({ key: c.id, label: c.name })),
            { key: 'uncategorized', label: 'Uncategorized' },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveCategory(t.key)}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                activeCategory===t.key ? 'bg-white text-purple-700 shadow-sm' : 'text-[#6B7280] hover:bg-white/50'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div className="rounded-2xl overflow-hidden border-2 border-[#312E80] bg-[#1C1A18]">
        <div className="px-5 py-2 flex items-center justify-between">
          <span className="text-white font-bold text-sm tracking-wide">INDIRECT FORECAST — GRAND TOTAL</span>
          <span className="text-purple-400 text-xs font-semibold">{viewingDraft ? '✏️' : '🔒'} {currentSourceLabel}</span>
        </div>
        <div className="grid grid-cols-6 bg-[#2E2C29]">
          {[
            {label:'BUDGET',value:gtBudget,color:'text-white'},
            {label:'COMMITMENT',value:gtCommit,color:'text-blue-400'},
            {label:'ACTUAL',value:gtActual,color:'text-[#1A7A4A]'},
            {label:'ETC',value:gtETC,color:'text-amber-400'},
            {label:'FORECAST (EAC)',value:gtEAC,color:'text-purple-400'},
            {label:'VARIANCE',value:gtBudget-gtEAC,color:gtBudget-gtEAC>=0?'text-emerald-400':'text-red-400'},
          ].map(s => (
            <div key={s.label} className="px-4 py-4 border-r border-[#1C1A18] last:border-0">
              <p className="text-xs text-[#9CA3AF] font-semibold tracking-wider mb-1">{s.label}</p>
              <p className={`text-lg font-black ${s.color}`}>{fmt(s.value)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Cost Codes Table — SINGLE Forecast column, no month/revision columns. */}
      {loading ? (
        <div className="text-center py-16 text-[#9CA3AF] text-sm">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#F5F1EA] border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-4 py-2.5">COST CODE</th>
                <th className="text-left px-3 py-2.5">DESCRIPTION</th>
                <th className="text-right px-3 py-2.5">BUDGET</th>
                <th className="text-right px-3 py-2.5 text-blue-600">COMMITMENT</th>
                <th className="text-right px-3 py-2.5 text-[#1A7A4A]">ACTUAL</th>
                <th className="text-right px-3 py-2.5 text-amber-600">ETC</th>
                <th className="text-right px-3 py-2.5 text-purple-600">EAC</th>
                <th className="text-right px-3 py-2.5 text-[#6B7280]">VARIANCE</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filteredTree.map((cc, idx) => {
                const isExpanded = expandedCC.has(cc.code)
                const ccCommitVal = ccCommitOf(cc)
                const ccActualVal = ccActualOf(cc)
                const ccEACVal    = ccEac(cc)
                const ccETCVal    = ccEtc(cc)
                const draftOnCC   = viewingDraft && draftEdits[cc.code] != null
                const hasDirectOverride = hasForecastEntry(cc.code)
                const variance    = cc.budget - ccEACVal
                const bgClass     = idx%2===0?'bg-white':'bg-[#FAFAFA]'

                return [
                  <tr key={cc.code} className={`border-b border-[#F3F4F6] ${bgClass} ${cc.subs.length?'cursor-pointer hover:bg-[#F5F1EA]':''}`}
                    onClick={() => cc.subs.length && toggleCC(cc.code)}>
                    <td className="px-4 py-2.5 font-mono font-bold text-purple-700">
                      <span className="flex items-center gap-1">
                        {cc.subs.length > 0 && <span className="text-[#9CA3AF] text-xs">{isExpanded?'▼':'▶'}</span>}
                        {cc.code}
                        {draftOnCC && <span className="text-[9px] text-purple-600 font-bold ml-1" title="Unsaved change this session">●unsaved</span>}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-semibold text-[#1C1A18]">{cc.description}</td>
                    <td className="px-3 py-2.5 text-right font-bold font-mono">{cc.budget>0?fmt(cc.budget):'—'}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-blue-600">{ccCommitVal>0?fmt(ccCommitVal):'—'}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-[#1A7A4A]">{ccActualVal>0?fmt(ccActualVal):'—'}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-amber-600">{ccETCVal!==0?fmt(ccETCVal):'—'}</td>
                    <td className="px-3 py-2.5 text-right font-bold font-mono text-purple-600">
                      {ccEACVal>0?fmt(ccEACVal):'—'}
                      {hasDirectOverride && <span className="ml-1 text-[9px] text-purple-500" title="Manual forecast set directly on this Cost Code — Sub Code totals below are not summed">●</span>}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-mono ${variance>=0?'text-emerald-600':'text-red-500'}`}>{cc.budget>0?fmt(variance):'—'}</td>
                    <td className="px-3 py-2.5 text-center">
                      <button onClick={e=>{e.stopPropagation();setEtcModal({
                        cost_code:cc.code, sub_code:null, description:cc.description,
                        budget:cc.budget, commitment:ccCommitVal, actual:ccActualVal,
                        po_first_date: poDatesByCC[cc.code]?.first,
                        po_last_date:  poDatesByCC[cc.code]?.last,
                        savedEac: viewingDraft ? draftPersistedRows[cc.code] : null,
                      })}} className="px-2 py-1 text-xs bg-purple-50 border border-purple-200 text-purple-600 rounded hover:bg-purple-100">
                        ETC
                      </button>
                    </td>
                  </tr>,

                  ...(isExpanded ? cc.subs.map((sub) => {
                    const subCommit = commitByCC[sub.code] || 0
                    const subActual = actualByCC[sub.code] || 0
                    const draftOnSub = viewingDraft && draftEdits[sub.code] != null
                    const subEAC    = effectiveEac(sub.code)
                    const subETC    = effectiveEtc(sub.code)
                    return (
                      <tr key={sub.code} className="border-b border-[#F3F4F6] bg-purple-50/20">
                        <td className="px-4 py-2 pl-8 font-mono text-xs text-purple-400">
                          ↳ {sub.code}
                          {draftOnSub && <span className="text-[9px] text-purple-600 font-bold ml-1" title="Unsaved change this session">●unsaved</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-[#6B7280]">{sub.description}</td>
                        <td className="px-3 py-2 text-right text-xs text-[#9CA3AF] italic">incl.</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-blue-600">{subCommit>0?fmt(subCommit):'—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[#1A7A4A]">{subActual>0?fmt(subActual):'—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-amber-600">{subETC!==0?fmt(subETC):'—'}</td>
                        <td className="px-3 py-2 text-right font-bold font-mono text-xs text-purple-600">{subEAC>0?fmt(subEAC):'—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs text-[#9CA3AF]">—</td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={()=>setEtcModal({
                            cost_code:cc.code, sub_code:sub.code, description:sub.description,
                            budget:cc.budget, commitment:subCommit, actual:subActual,
                            po_first_date: poDatesByCC[sub.code]?.first,
                            po_last_date:  poDatesByCC[sub.code]?.last,
                            savedEac: viewingDraft ? draftPersistedRows[sub.code] : null,
                          })} className="px-2 py-1 text-xs bg-purple-50 border border-purple-200 text-purple-600 rounded hover:bg-purple-100">
                            ETC
                          </button>
                        </td>
                      </tr>
                    )
                  }) : [])
                ]
              })}
            </tbody>
            <tfoot className="bg-[#312E80]">
              <tr>
                <td colSpan={2} className="px-4 py-3 text-xs font-bold text-purple-200 uppercase">Grand Total</td>
                <td className="px-3 py-3 text-right font-bold text-white font-mono">{fmt(gtBudget)}</td>
                <td className="px-3 py-3 text-right font-bold text-blue-300 font-mono">{fmt(gtCommit)}</td>
                <td className="px-3 py-3 text-right font-bold text-[#1A7A4A] font-mono">{fmt(gtActual)}</td>
                <td className="px-3 py-3 text-right font-bold text-amber-300 font-mono">{fmt(gtETC)}</td>
                <td className="px-3 py-3 text-right font-bold text-purple-300 font-mono">{fmt(gtEAC)}</td>
                <td className={`px-3 py-3 text-right font-bold font-mono ${gtBudget-gtEAC>=0?'text-emerald-300':'text-red-300'}`}>{fmt(gtBudget-gtEAC)}</td>
                <td/>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {etcModal && (
        <ETCModal
          row={etcModal}
          draft={draftEdits[etcModal.sub_code || etcModal.cost_code]}
          readOnly={!viewingDraft}
          onClose={() => setEtcModal(null)}
          onDraftChange={handleDraftChange}
          onClearDraft={handleClearDraft}
        />
      )}

      {showCommitModal && (
        <CommitModal
          periodLabel={draftForecastDate ? monthYearLabel(draftForecastDate) : ''}
          committing={busy}
          onClose={() => setShowCommitModal(false)}
          onConfirm={handleCommit}
        />
      )}

      {showRevisionModal && (
        <CreateRevisionModal
          sourceLabel={currentSourceLabel}
          existingDraftInfo={pendingRevisionParams ? { forecast_date: draftForecastDate } : null}
          creating={busy}
          onClose={() => { setShowRevisionModal(false); setPendingRevisionParams(null) }}
          onConfirm={handleCreateRevision}
          onContinueExistingDraft={handleContinueExistingDraft}
          onDiscardAndCreate={handleDiscardAndCreateRevision}
        />
      )}
    </div>
  )
}
